// Recovery for a tab that outlived a deploy. Each Fly image ships only the
// newest build's hashed /vite/assets, so a tab loaded before a deploy still
// asks for the previous build's chunk names; the first lazy route (or other
// dynamic import) it opens fails to load. React.lazy caches that rejection, so
// the route stays broken until a full reload fetches the new build — which is
// exactly what we do, once. A per-tab timestamp in sessionStorage stops a loop
// when the build we reload into is itself missing the chunk.

const RELOAD_KEY = "jynio.chunkReloadAt";
const RELOAD_GUARD_MS = 10_000;

// Each engine words a failed dynamic import differently; Vite adds its own for
// a CSS preload, and a missing chunk served as an HTML page gives a MIME error.
const CHUNK_ERROR_PATTERNS = [
  /Importing a module script failed/i, // WebKit (Safari)
  /Failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /Unable to preload CSS/i, // Vite's preload helper
  /is not a valid JavaScript MIME type/i,
];

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

export type ChunkRecovery = "reloading" | "gave-up";

let reloading = false;

// "reloading": a reload is under way (started now or by an earlier call on this
// page), so the failure is expected and not worth reporting. "gave-up": the
// page we reloaded into failed again within the guard window, or there is no
// sessionStorage to guard with — the caller should surface and report it.
export function recoverFromChunkError(
  reload: () => void = () => window.location.reload(),
  now: () => number = Date.now,
): ChunkRecovery {
  if (reloading) return "reloading";
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY));
    if (last && now() - last < RELOAD_GUARD_MS) return "gave-up";
    window.sessionStorage.setItem(RELOAD_KEY, String(now()));
  } catch {
    return "gave-up";
  }
  reloading = true;
  reload();
  return "reloading";
}

// Vite dispatches `vite:preloadError` when a dynamic import through its preload
// helper fails — every lazy route in this app, and the other dynamic imports.
// The event is deliberately not cancelled: preventDefault() makes the import
// resolve to undefined, and WebKit then throws a second, misleading error. The
// original error still reaches the ErrorBoundary, which sees the reload is
// under way and neither reports it nor retries.
export function installChunkReloadHandler(reload?: () => void) {
  window.addEventListener("vite:preloadError", () => {
    recoverFromChunkError(reload);
  });
}

// Test hook: module state is per page load, so specs simulate a fresh load.
export function resetChunkRecovery() {
  reloading = false;
}
